/**
 * Waitlist — real, shareable /waitlist route.
 *
 * Why this route exists:
 *   The homepage exposes the waitlist as an in-page anchor
 *   (/#waitlist). That works for scroll-to-form on the landing
 *   page but 404s when someone deep-links "/waitlist" from a print
 *   flyer, QR code, Facebook post, or recruiting kit link. This
 *   page provides a REAL standalone URL that:
 *     * Cold-loads from any external source
 *     * Sets proper <title> + <meta> + OG tags for link previews
 *     * Reuses the canonical `<WaitlistForm />` — no fork, no
 *       reimplementation. The Turnstile mount, marketing opt-in
 *       checkbox, and honest launch-framing copy all flow from
 *       the same component that renders inside the /#waitlist
 *       anchor section on the landing page.
 *
 * Single-source guarantee:
 *   `<WaitlistForm />` is imported here from the SAME module
 *   Landing.jsx imports it from. A drift-guard test in
 *   `Waitlist.singleSource.test.jsx` scans the repo for stray
 *   `function WaitlistForm(` definitions and fails if a second
 *   copy ever appears.
 *
 * /#waitlist compatibility:
 *   The homepage anchor and its embedded `<WaitlistForm />`
 *   continue to work byte-identically. This page is additive —
 *   the anchor is not removed, no in-app CTA has been migrated
 *   yet. Users typing "/waitlist" now get a real page; users
 *   scrolling to the anchor on the homepage still get the same
 *   experience.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import PublicPageShell from "@/components/PublicPageShell";
import WaitlistForm from "@/components/WaitlistForm";
import WinnerSlotCard from "@/components/WinnerSlotCard";

// Meta copy is intentionally short + honest. Matches the launch
// framing already surfaced at the /#waitlist anchor on the homepage
// so a user who lands here directly from a link preview sees the
// same message they'd see scrolling through the homepage.
const WAITLIST_TITLE = "Join the PooPluckers waitlist";
const WAITLIST_DESCRIPTION =
  "We're rolling out neighborhood by neighborhood — drop your info and we'll email you the moment PooPluckers is live in your ZIP.";
const WAITLIST_HERO_EYEBROW = "Pre-launch";
const WAITLIST_HERO_HEADLINE = "Get on the waitlist.";
const WAITLIST_HERO_INTRO = WAITLIST_DESCRIPTION;

export default function Waitlist() {
  return (
    <PublicPageShell>
      <Helmet>
        <title>{WAITLIST_TITLE}</title>
        <meta name="description" content={WAITLIST_DESCRIPTION} />
        <meta property="og:title" content={WAITLIST_TITLE} />
        <meta property="og:description" content={WAITLIST_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={WAITLIST_TITLE} />
        <meta name="twitter:description" content={WAITLIST_DESCRIPTION} />
      </Helmet>

      <section
        data-testid="waitlist-page-hero-section"
        aria-labelledby="waitlist-page-hero-headline"
      >
        <div className="pp-wrap py-10 md:py-16 text-center">
          <span
            className="pp-eyebrow"
            data-testid="waitlist-page-hero-eyebrow"
          >
            <span className="dot" />
            {WAITLIST_HERO_EYEBROW}
          </span>
          <h1
            id="waitlist-page-hero-headline"
            className="pp-h1 mt-6 mx-auto max-w-3xl"
            data-testid="waitlist-page-hero-headline"
          >
            {WAITLIST_HERO_HEADLINE}
          </h1>
          <p
            className="pp-sub mt-5 mx-auto max-w-2xl"
            data-testid="waitlist-page-hero-intro"
          >
            {WAITLIST_HERO_INTRO}
          </p>
        </div>
      </section>

      <section
        data-testid="waitlist-page-form-section"
        aria-labelledby="waitlist-page-form-heading"
      >
        <div className="pp-wrap pb-16 md:pb-24 max-w-2xl">
          <h2
            id="waitlist-page-form-heading"
            className="sr-only"
          >
            Waitlist signup form
          </h2>
          <WaitlistForm />
        </div>
      </section>
      <WinnerSlotCard winnerRank={7} />
    </PublicPageShell>
  );
}
